import { useEffect, useRef, useState } from 'react';

/**
 * Crossfades a region whenever `stateKey` changes: opacity dips to 0 over
 * `outMs`, stays dark just long enough for the content swap underneath to
 * happen unseen, then returns over `inMs`. Used by the course preview's
 * "See it fill in" beat to drain the demo values back to empty.
 *
 * Always renders the current `children` (nothing is snapshotted or held
 * stale). Respects prefers-reduced-motion (no transition).
 */
export function OpenFadeRegion({
  stateKey,
  children,
  outMs = 300,
  inMs = 300,
}: {
  stateKey: string;
  children: React.ReactNode;
  outMs?: number;
  inMs?: number;
}) {
  const [fading, setFading] = useState(false);
  const prevKey = useRef(stateKey);

  useEffect(() => {
    if (prevKey.current === stateKey) return;
    prevKey.current = stateKey;
    setFading(true);
    const t = setTimeout(() => setFading(false), outMs + 50);
    return () => clearTimeout(t);
  }, [stateKey, outMs]);

  return (
    <div
      className="motion-reduce:transition-none"
      style={{
        opacity: fading ? 0 : 1,
        transitionProperty: 'opacity',
        transitionDuration: `${fading ? outMs : inMs}ms`,
      }}
    >
      {children}
    </div>
  );
}
